import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { FreshdeskSolutionService, LinkInformationFreshdeskArticle } from '../../../services/freshdesk-solutions.service';
import { DxDataGridModule, DxButtonModule } from 'devextreme-angular';
import { NgIf } from '@angular/common';
import { bind } from 'libs/utils/src/lib/generic/hard-bind';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import Swal from 'sweetalert2';
import { getErrorSwal, showErrorSwal } from '@asamco/utils';

@Component({
  selector: 'app-link-edit-support-freshdesk-admin',
  standalone: true,
  imports: [DxDataGridModule, DxButtonModule, NgIf, ReactiveFormsModule],
  templateUrl: './link-edit-support-freshdesk-admin.component.html',
  styleUrls: ['./link-edit-support-freshdesk-admin.component.scss'],
})
export class LinkEditSupportFreshdeskAdminComponent {
  @Input() title = '[Admin] Manage Freshdesk Articles for Link Information Template';

  /** Modal visibility */
  public visible$ = false;

  /** Current linkId */
  private linkId: number | null = null;

  /** Local array for DevExtreme grid — keep as plain array to preserve object identity */
  public articles: LinkInformationFreshdeskArticle[] = [];

  /** Add form (simple) */
  public addForm = this.fb.group({
    articleId: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
  });

  constructor(
    private service: FreshdeskSolutionService,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder
  ) { }

  /** Open modal */
  public open(linkId: number) {
    this.linkId = linkId;
    this.visible$ = true;
    this.loadArticles();
  }

  /** Close modal */
  public close() {
    this.visible$ = false;
  }

  /** Load articles from backend */
  private loadArticles() {
    if (!this.linkId) return;
    this.service.getLinkInformationArticles(this.linkId).subscribe(articles => {
      // keep the same objects coming from backend; ensure sorted by index
      this.articles = [...articles.sort((a, b) => a.index - b.index)];
      this.cdr.markForCheck();
    });
  }

  /** Add new article — inline form instead of prompt */
  loading = false;

  public addEntry() {
    if (!this.linkId) return;

    if (this.addForm.invalid) {
      this.addForm.markAllAsTouched(); // show errors
      return;
    }

    const articleId = parseInt(this.addForm.value.articleId, 10);
    const index = this.articles.length + 1;

    this.loading = true; // start spinner

    this.service.createArticleLink(this.linkId, articleId, index).subscribe({
      next: linkInfo => {
        this.loadArticles();
        Swal.fire({ icon: 'success', text: 'Successfully added article link.' });
      },
      error: (err) => {
        showErrorSwal(err);
        this.loading = false;
      },
      complete: () => {
        this.loading = false; // stop spinner
        this.addForm.reset();  // optionally clear form
      }
    });
  }

  refreshArticles() {
    this.service.refreshArticles(this.linkId).subscribe({
      next: () => {
        Swal.fire({ icon: 'success', text: 'Successfully refreshed articles from Freshdesk.' });
        this.loadArticles();
      },
      error: (err) => {
        showErrorSwal(err);
      }
    });
  }

  /** Delete article */
  @bind
  public deleteEntry(article: any) {
    if (!this.linkId) return;

    Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to delete this article from this link information template?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.service.deleteArticleLink(this.linkId, article.data.articleId).subscribe({
          next: () => {
            this.articles = this.articles.filter(a => a.articleId !== article.data.articleId);
            this.cdr.markForCheck();
            Swal.fire('Deleted!', 'The article link has been removed.', 'success');
          },
          error: (err) => {
            showErrorSwal(err);
          }
        });
      }
    });
  }

  /**
   * Restored reorder logic — operates on the same array objects DevExtreme has,
   * so `indexOf` works reliably. Kept the sequential reindexing and backend batch update.
   */
  @bind
  public onReorder(e: any) {
    // Defensive: ensure we have visible rows and item data
    try {
      const visibleRows = e.component.getVisibleRows();
      if (!visibleRows || !Array.isArray(visibleRows)) return;

      // Ensure local array is sorted by index before computing positions
      this.articles.sort((a, b) => a.index - b.index);

      // Find destination and source indices in the *same* array instances
      const toRow = visibleRows[e.toIndex];
      if (!toRow) return;

      const toIndex = this.articles.indexOf(toRow.data);
      const fromIndex = this.articles.indexOf(e.itemData);

      if (fromIndex < 0 || toIndex < 0) {
        // if we didn't find them, abort gracefully
        console.warn('onReorder: could not find from/to indices', { fromIndex, toIndex });
        return;
      }

      // Move item in-place on the articles array (preserving references)
      const [movedItem] = this.articles.splice(fromIndex, 1);
      this.articles.splice(toIndex, 0, movedItem);

      // Reassign sequential indices
      this.articles.forEach((item, index) => {
        item.index = index + 1;
      });

      // Persist new order to backend
      if (!this.linkId) return;
      const payload = this.articles.map(a => ({ articleId: a.articleId, index: a.index }));
      this.service.batchUpdateIndices(this.linkId, payload).subscribe({
        next: () => {
          // optionally show success
        },
        error: (err) => {
          showErrorSwal(err)
        }
      });

      // Trigger change detection
      this.cdr.markForCheck();
    } catch (err) {
      console.error('Error in onReorder', err);
    }
  }
}
